import {
    useCallback,
    useEffect,
    useState,
    useMemo,
    useRef
} from "react";
import {
    useWindowDimensions,
    TouchableOpacity,
    ScrollView,
    Platform,
    Image,
    View
} from "react-native";
import type IShareInstagramModalProps from "./type";
import stylesheet from "./stylesheet";
import {
    type ITextAreaInputRef,
    NCoreUIKitSnackBar,
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    TextAreaInput,
    Button,
    Dialog,
    Modal,
    Text
} from "ncore-ui-kit";
import {
    MessageCircleIcon,
    ChevronRightIcon,
    ChevronLeftIcon,
    LayoutGridIcon,
    ArrowLeftIcon,
    BookmarkIcon,
    FolderIcon,
    LayersIcon,
    CheckIcon,
    HeartIcon,
    ImageIcon,
    PlusIcon,
    SendIcon,
    XIcon
} from "lucide-react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import Svg, {
    Circle,
    Path,
    Rect
} from "react-native-svg";
import {
    stripMarkdownForInstagram,
    extractImagesFromMarkdown
} from "../../utils/instagramMarkdown";
import RESTServiceContext from "../../services/restAPI/context";
import DriveSelectorModal from "../driveSelectorModal";import SmartCoverImage from "../smartCoverImage";

const normalizePathKey = (pathOrUrl?: string) => {
    if(!pathOrUrl) {
        return "";
    }

    const cleanUrl = pathOrUrl.split("?")[0] || "";
    const withoutOrigin = cleanUrl.replace(/^https?:\/\/[^/]+/, "");
    const withoutLeadingSlash = withoutOrigin.replace(/^\/+/, "");

    if(withoutLeadingSlash.startsWith("uploads/")) {
        return withoutLeadingSlash.replace(/^uploads\//, "");
    }

    return withoutLeadingSlash;
};

const getPhotoUrl = (photo?: string | { path?: string; url?: string }, authToken?: string) => {
    let targetUrl = typeof photo === "string" ? photo : (photo?.url || photo?.path || "");

    if(!targetUrl) {
        return "";
    }

    if(targetUrl.includes("/uploads/drive/")) {
        targetUrl = targetUrl.replace("/uploads/drive/", "/drive/");
    }

    const isDrive = targetUrl.includes("/drive/");

    if(isDrive) {
        if(!targetUrl.startsWith("http")) {
            const cleanPath = targetUrl.startsWith("/") ? targetUrl : `/${targetUrl}`;
            targetUrl = `${process.env.STORAGE_URL}${cleanPath}`;
        }

        if(authToken && !targetUrl.includes("token=")) {
            const separator = targetUrl.includes("?") ? "&" : "?";
            targetUrl = `${targetUrl}${separator}token=${authToken}`;
        }

        return targetUrl;
    }

    if(targetUrl.startsWith("http")) {
        return targetUrl;
    }

    const cleanPath = targetUrl.startsWith("/uploads")
        ? targetUrl
        : `/uploads${targetUrl.startsWith("/") ? "" : "/"}${targetUrl}`;

    return `${process.env.STORAGE_URL}${cleanPath}`;
};

const CustomInstagramIcon = ({
    color = "#E1306C",
    size = 20
}: {
    color?: string;
    size?: number;
}) => {
    return <Svg
        strokeLinejoin="round"
        strokeLinecap="round"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke={color}
        height={size}
        width={size}
        fill="none"
    >
        <Rect
            height={20}
            width={20}
            rx={5}
            ry={5}
            x={2}
            y={2}
        />
        <Path
            d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"
        />
        <Circle
            fill={color}
            cx={17.5}
            cy={6.5}
            r={1.5}
        />
    </Svg>;
};

const ShareInstagramModal = ({
    isAlreadyPublished,
    initialShareMode,
    coverPhotoPath,
    availableData,
    shortContent,
    postPhotos,
    targetType,
    isVisible,
    onSuccess,
    targetID,
    onClose,
    title
}: IShareInstagramModalProps) => {
    const {
        width
    } = useWindowDimensions();
    const isMobile = width < 768;

    const {
        activeLocale,
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const [
        authToken,
        setAuthToken
    ] = useState<string>("");

    const trData = useMemo(() => {
        return availableData?.find(d => d.language === "tr-TR");
    }, [availableData]);

    const enData = useMemo(() => {
        return availableData?.find(d => d.language === "en-US");
    }, [availableData]);

    const hasTurkish = Boolean(trData?.title || trData?.shortContent || trData?.content);
    const hasEnglish = Boolean(enData?.title || enData?.shortContent || enData?.content);
    const isMultiLanguage = hasTurkish && hasEnglish;

    const buildCaption = useCallback((mode: "tr" | "en" | "both") => {
        const targetTrTitle = trData?.title || title;
        const targetEnTitle = enData?.title || title;

        const rawTrBody = trData?.shortContent || trData?.content || shortContent || "";
        const rawEnBody = enData?.shortContent || enData?.content || shortContent || "";

        const cleanTrBody = stripMarkdownForInstagram(rawTrBody);
        const cleanEnBody = stripMarkdownForInstagram(rawEnBody);

        const trSection = `📢 ${targetTrTitle}\n\n${cleanTrBody ? `${cleanTrBody}\n\n` : ""}`;
        const enSection = `📢 ${targetEnTitle}\n\n${cleanEnBody ? `${cleanEnBody}\n\n` : ""}`;

        if(mode === "both" && isMultiLanguage) {
            const trTag = targetType === "news" ? "Haber" : "Duyuru";
            const enTag = targetType === "news" ? "News" : "Announcement";

            return `${trSection}═══════════════════════\n\n${enSection}#GençMÜSİAD #GencMUSIAD #MÜSİAD #${trTag} #${enTag}`;
        }

        if(mode === "en") {
            const enTag = targetType === "news" ? "News" : "Announcement";
            return `${enSection}#GencMUSIAD #MÜSİAD #${enTag}`;
        }

        const trTag = targetType === "news" ? "Haber" : "Duyuru";
        return `${trSection}#GençMÜSİAD #MÜSİAD #${trTag}`;
    }, [
        isMultiLanguage,
        shortContent,
        targetType,
        trData,
        enData,
        title
    ]);

    const initialLangMode = activeLocale === "en-US" && hasEnglish ? "en" : "tr";

    const [
        selectedLangMode,
        setSelectedLangMode
    ] = useState<"tr" | "en" | "both">(initialLangMode);

    const [
        shareMode,
        setShareMode
    ] = useState<"single" | "carousel" | "triple">("single");

    const [
        carouselPreviewIndex,
        setCarouselPreviewIndex
    ] = useState<number>(0);

    const [
        activePreviewTab,
        setActivePreviewTab
    ] = useState<"grid" | 0 | 1 | 2>("grid");

    const [
        activeSlotIndex,
        setActiveSlotIndex
    ] = useState<number>(0);

    const [
        modalStep,
        setModalStep
    ] = useState<"select_mode" | "editor">("select_mode");

    const [
        tripleImages,
        setTripleImages
    ] = useState<Array< |
    {
        displayUrl: string;
        label: string;
        url: string;
        key: string;
    } |
    null>>([
        null,
        null,
        null
    ]);

    const [
        customImages,
        setCustomImages
    ] = useState<Array<{
        displayUrl: string;
        label: string;
        url: string;
        key: string;
    }>>([]);

    const [
        isDriveModalOpen,
        setIsDriveModalOpen
    ] = useState<boolean>(false);

    const [
        draggedSlotIndex,
        setDraggedSlotIndex
    ] = useState<number | null>(null);

    const [
        selectedImage,
        setSelectedImage
    ] = useState<string>(coverPhotoPath || postPhotos?.[0]?.path || "");

    const [
        caption,
        setCaption
    ] = useState<string>(() => buildCaption(initialLangMode));

    const textAreaRef = useRef<ITextAreaInputRef>(null);

    const [
        isSubmitting,
        setIsSubmitting
    ] = useState<boolean>(false);

    const [
        isConfirmDialogOpen,
        setIsConfirmDialogOpen
    ] = useState<boolean>(false);

    const allImages = useMemo(() => {
        const list: Array<{
            displayUrl: string;
            label: string;
            url: string;
            key: string;
        }> = [];
        const seenKeys = new Set<string>();

        if(coverPhotoPath) {
            const key = normalizePathKey(coverPhotoPath);

            if(key) {
                seenKeys.add(key);
                list.push({
                    displayUrl: getPhotoUrl(coverPhotoPath, authToken),
                    label: localize("instagram-cover-image-label"),
                    url: coverPhotoPath,
                    key
                });
            }
        }

        if(postPhotos && postPhotos.length > 0) {
            postPhotos.forEach((photo, idx) => {
                if(photo.path) {
                    const key = normalizePathKey(photo.path);

                    if(key && !seenKeys.has(key)) {
                        seenKeys.add(key);
                        list.push({
                            label: `${localize("post-photos-title")} #${idx + 1}`,
                            displayUrl: getPhotoUrl(photo.path, authToken),
                            url: photo.path,
                            key
                        });
                    }
                }
            });
        }

        if(availableData) {
            for(const item of availableData) {
                const extracted = extractImagesFromMarkdown(item.content);

                for(const imgUrl of extracted) {
                    const key = normalizePathKey(imgUrl);

                    if(key && !seenKeys.has(key)) {
                        seenKeys.add(key);
                        list.push({
                            label: localize("instagram-content-image-label"),
                            displayUrl: getPhotoUrl(imgUrl, authToken),
                            url: imgUrl,
                            key
                        });
                    }
                }
            }
        }

        if(customImages && customImages.length > 0) {
            customImages.forEach(img => {
                if(img.key && !seenKeys.has(img.key)) {
                    seenKeys.add(img.key);
                    list.push(img);
                }
            });
        }

        return list;
    }, [
        coverPhotoPath,
        availableData,
        customImages,
        postPhotos,
        authToken,
        localize
    ]);

    const isSelectedImageJpeg = useMemo(() => {
        if(!selectedImage) {
            return true;
        }
        const cleanUrl = (selectedImage.split("?")[0] ?? "").toLowerCase();
        return cleanUrl.endsWith(".jpg") || cleanUrl.endsWith(".jpeg");
    }, [selectedImage]);

    const isAllCarouselImagesJpeg = useMemo(() => {
        if(shareMode !== "carousel") {
            return true;
        }

        return allImages.every(img => {
            const cleanUrl = (img.url.split("?")[0] ?? "").toLowerCase();
            return cleanUrl.endsWith(".jpg") || cleanUrl.endsWith(".jpeg");
        });
    }, [
        allImages,
        shareMode
    ]);

    const isAllTripleImagesJpeg = useMemo(() => {
        if(shareMode !== "triple") {
            return true;
        }

        return tripleImages.every(img => {
            if(!img) {
                return true;
            }
            const cleanUrl = (img.url.split("?")[0] ?? "").toLowerCase();
            return cleanUrl.endsWith(".jpg") || cleanUrl.endsWith(".jpeg");
        });
    }, [
        tripleImages,
        shareMode
    ]);

    useEffect(() => {
        const loadAuthToken = async () => {
            const token = await AsyncStorage.getItem("authToken");

            if(token) {
                setAuthToken(token);
            }
        };

        if(isVisible) {
            loadAuthToken();
        }
    }, [isVisible]);

    useEffect(() => {
        if(!isVisible) {
            return;
        }

        const initialMode = activeLocale === "en-US" && hasEnglish ? "en" : "tr";
        setSelectedLangMode(initialMode);
        const freshCaption = buildCaption(initialMode);
        setCaption(freshCaption);
        textAreaRef.current?.updateValue(freshCaption);
        const initialImg = coverPhotoPath || (postPhotos?.[0]?.path ?? "");

        if(initialImg) {
            setSelectedImage(initialImg);
        }

        const firstThree = allImages.slice(0, 3);
        setTripleImages([
            firstThree[0] || null,
            firstThree[1] || null,
            firstThree[2] || null
        ]);

        if(initialShareMode) {
            setShareMode(initialShareMode);
            setModalStep("editor");
        } else {
            setModalStep("select_mode");
            if(allImages.length >= 3) {
                setShareMode("triple");
            } else if(allImages.length === 2) {
                setShareMode("carousel");
            } else {
                setShareMode("single");
            }
        }

        setCarouselPreviewIndex(0);
        setActivePreviewTab("grid");
        setActiveSlotIndex(0);
    }, [
        initialShareMode,
        coverPhotoPath,
        activeLocale,
        buildCaption,
        hasEnglish,
        postPhotos,
        allImages,
        isVisible
    ]);

    const handleSlotDragStart = (index: number, e: React.DragEvent<HTMLElement>) => {
        setDraggedSlotIndex(index);
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", index.toString());
    };

    const handleSlotDragOver = (e: React.DragEvent<HTMLElement>) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
    };

    const handleSlotDrop = (targetIndex: number, e: React.DragEvent<HTMLElement>) => {
        e.preventDefault();

        if(draggedSlotIndex === null || draggedSlotIndex === targetIndex) {
            setDraggedSlotIndex(null);
            return;
        }

        const updated = [...tripleImages];
        const [
            draggedItem
        ] = updated.splice(draggedSlotIndex, 1);

        if(draggedItem) {
            updated.splice(targetIndex, 0, draggedItem);
            setTripleImages(updated);
        }

        setDraggedSlotIndex(null);
    };

    const handleSlotDragEnd = () => {
        setDraggedSlotIndex(null);
    };

    const handleSwapSlots = (indexA: number, indexB: number) => {
        if(indexA < 0 || indexA >= tripleImages.length || indexB < 0 || indexB >= tripleImages.length) {
            return;
        }

        const updated = [...tripleImages];
        const itemA = updated[indexA];
        const itemB = updated[indexB];

        if(!itemA || !itemB) {
            return;
        }

        updated[indexA] = itemB;
        updated[indexB] = itemA;
        setTripleImages(updated);
    };

    const handleDriveSelect = (url: string) => {
        setIsDriveModalOpen(false);
        if(!url) {
            return;
        }

        const key = normalizePathKey(url);
        const newItem = {
            displayUrl: getPhotoUrl(url, authToken),
            label: "Drive",
            url,
            key
        };

        setCustomImages(prev => {
            if(prev.some(img => img.key === key)) {
                return prev;
            }
            return [
                ...prev,
                newItem
            ];
        });

        if(shareMode === "triple") {
            const updated = [...tripleImages];
            updated[activeSlotIndex] = newItem;
            setTripleImages(updated);
        } else if(shareMode === "single") {
            setSelectedImage(url);
        }
    };

    const handleRemoveSlotImage = (index: number) => {
        const updated = [...tripleImages];
        updated[index] = null;
        setTripleImages(updated);
    };

    const handleSelectImageForSlot = (imageItem: typeof allImages[0]) => {
        if(shareMode === "single") {
            setSelectedImage(imageItem.url);
            return;
        }

        if(shareMode === "triple") {
            const existingIndex = tripleImages.findIndex(img => img?.key === imageItem.key);

            if(existingIndex !== -1 && existingIndex !== activeSlotIndex) {
                const updated = [...tripleImages];
                const temp = updated[activeSlotIndex] || null;
                updated[activeSlotIndex] = updated[existingIndex] || null;
                updated[existingIndex] = temp;
                setTripleImages(updated);
                return;
            }

            const updated = [...tripleImages];
            updated[activeSlotIndex] = imageItem;
            setTripleImages(updated);
        }
    };

    const handleLanguageChange = (mode: "tr" | "en" | "both") => {
        setSelectedLangMode(mode);
        const freshCaption = buildCaption(mode);
        setCaption(freshCaption);
        textAreaRef.current?.updateValue(freshCaption);
    };

    const availableTags = useMemo(() => {
        const typeTag = targetType === "news" ? "#Haber" : "#Duyuru";

        return [
            "#GençMÜSİAD",
            "#MÜSİAD",
            typeTag,
            "#Konya"
        ];
    }, [targetType]);

    const handleToggleTag = (tag: string) => {
        let nextCaption = caption;

        if(caption.includes(tag)) {
            const escapedTag = tag.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
            const tagRegex = new RegExp(`[ \\t]*${escapedTag}`, "g");
            nextCaption = caption.replace(tagRegex, "").replace(/[ \\t]{2,}/g, " ");
            nextCaption = nextCaption.split("\n").map(line => line.trimEnd()).join("\n").replace(/\n{3,}/g, "\n\n").trim();
        } else {
            const trimmedCaption = caption.trimEnd();

            if(!trimmedCaption) {
                nextCaption = tag;
            } else if(!trimmedCaption.includes("#")) {
                nextCaption = `${trimmedCaption}\n\n${tag}`;
            } else {
                nextCaption = `${trimmedCaption} ${tag}`;
            }
        }

        setCaption(nextCaption);
        textAreaRef.current?.updateValue(nextCaption);
    };

    const handleShare = useCallback(async () => {
        setIsConfirmDialogOpen(false);
        if(isSubmitting) {
            return;
        }

        const isTripleActive = shareMode === "triple";
        const isCarouselActive = shareMode === "carousel";

        if(isTripleActive) {
            const hasEmptySlot = tripleImages.some(img => !img || !img.url);

            if(hasEmptySlot || tripleImages.length < 3) {
                NCoreUIKitSnackBar.open({
                    title: localize("instagram-triple-fill-all-slots")
                });
                return;
            }

            if(!isAllTripleImagesJpeg) {
                NCoreUIKitSnackBar.open({
                    title: localize("instagram-invalid-image-format-dialog")
                });
                return;
            }
        } else if(isCarouselActive) {
            if(allImages.length < 2) {
                NCoreUIKitSnackBar.open({
                    title: localize("instagram-carousel-not-enough-images")
                });
                return;
            }

            if(!isAllCarouselImagesJpeg) {
                NCoreUIKitSnackBar.open({
                    title: localize("instagram-invalid-image-format-dialog")
                });
                return;
            }
        } else if(!isSelectedImageJpeg) {
            NCoreUIKitSnackBar.open({
                title: localize("instagram-invalid-image-format-dialog")
            });
            return;
        }

        const targetImageUrls = isTripleActive
            ? (tripleImages.filter(Boolean) as Array<typeof allImages[0]>).map(img => img.url)
            : (isCarouselActive ? allImages.map(img => img.url) : undefined);
        const targetImageUrl = (!isTripleActive && !isCarouselActive)
            ? (selectedImage || coverPhotoPath)
            : undefined;

        const handleShareError = (message?: string) => {
            if(message === "instagram-daily-quota-exceeded") {
                NCoreUIKitSnackBar.open({
                    title: localize("instagram-daily-quota-exceeded")
                });
                return;
            }

            if(message === "instagram-invalid-aspect-ratio") {
                NCoreUIKitSnackBar.open({
                    title: localize("instagram-invalid-aspect-ratio")
                });
                return;
            }

            if(message === "unsupported-image-format" || message === "instagram-invalid-image-format") {
                NCoreUIKitSnackBar.open({
                    title: localize("unsupported-image-format")
                });
                return;
            }

            NCoreUIKitSnackBar.open({
                title: localize("error-fetching-data")
            });
        };

        try {
            setIsSubmitting(true);
            if(targetType === "news") {
                const response = await RESTServiceContext.action("shareNewsOnInstagram", {
                    imageUrls: targetImageUrls,
                    force: isAlreadyPublished,
                    imageUrl: targetImageUrl,
                    isGrid: isTripleActive,
                    newsID: targetID,
                    caption
                });

                if(response?.payload) {
                    NCoreUIKitSnackBar.open({
                        title: localize("instagram-share-success")
                    });
                    onSuccess(response.payload.instagramPostUrl, response.payload.instagramPostId);
                    onClose();
                } else {
                    handleShareError(response?.message);
                }
            } else {
                const response = await RESTServiceContext.action("shareAnnouncementOnInstagram", {
                    imageUrls: targetImageUrls,
                    force: isAlreadyPublished,
                    announcementID: targetID,
                    imageUrl: targetImageUrl,
                    isGrid: isTripleActive,
                    caption
                });

                if(response?.payload) {
                    NCoreUIKitSnackBar.open({
                        title: localize("instagram-share-success")
                    });
                    onSuccess(response.payload.instagramPostUrl, response.payload.instagramPostId);
                    onClose();
                } else {
                    handleShareError(response?.message);
                }
            }
        } catch(error) {
            console.error(error);
            const errorObj = error as { message?: string } | undefined;
            handleShareError(errorObj?.message);
        } finally {
            setIsSubmitting(false);
        }
    }, [
        isAllCarouselImagesJpeg,
        isAllTripleImagesJpeg,
        isSelectedImageJpeg,
        isAlreadyPublished,
        coverPhotoPath,
        selectedImage,
        tripleImages,
        isSubmitting,
        targetType,
        shareMode,
        allImages,
        onSuccess,
        localize,
        targetID,
        caption,
        onClose
    ]);

    const renderSelectMode = () => {
        return <View
            style={[
                stylesheet.selectionContainer,
                {
                    marginBottom: spaces.spacingLg,
                    gap: spaces.spacingLg
                }
            ]}
        >
            <Text
                customColor={colors.content.text.mid}
                variant="bodyMediumSize"
            >
                {localize("instagram-share-select-type-subtitle")}
            </Text>
            <View
                style={{
                    gap: spaces.spacingMd
                }}
            >
                <TouchableOpacity
                    style={[
                        stylesheet.selectionCard,
                        {
                            borderColor: shareMode === "triple" ? colors.project.secondaryButton : colors.content.border.default,
                            backgroundColor: colors.content.container.subtle,
                            borderRadius: radiuses.md,
                            borderWidth: borders.line,
                            padding: spaces.spacingLg,
                            gap: spaces.spacingMd
                        }
                    ]}
                    onPress={() => {
                        setShareMode("triple");
                        setModalStep("editor");
                    }}
                    activeOpacity={0.7}
                >
                    <View
                        style={[
                            stylesheet.selectionCardLeft,
                            {
                                gap: spaces.spacingMd
                            }
                        ]}
                    >
                        <View
                            style={[
                                stylesheet.selectionCardIcon,
                                {
                                    backgroundColor: colors.project.secondaryButton,
                                    borderRadius: radiuses.full
                                }
                            ]}
                        >
                            <LayoutGridIcon
                                color={colors.project.brandWhite}
                                size={24}
                            />
                        </View>
                        <View
                            style={[
                                stylesheet.selectionCardBody,
                                {
                                    gap: spaces.spacingXs
                                }
                            ]}
                        >
                            <Text
                                customColor={colors.content.text.high}
                                variant="titleMediumSize"
                            >
                                {localize("instagram-share-mode-triple")}
                            </Text>
                            <Text
                                customColor={colors.content.text.low}
                                variant="bodySmallSize"
                            >
                                {localize("instagram-share-mode-triple-desc")}
                            </Text>
                        </View>
                    </View>
                    <ChevronRightIcon
                        color={colors.content.icon.mid}
                        size={20}
                    />
                </TouchableOpacity>
                <TouchableOpacity
                    style={[
                        stylesheet.selectionCard,
                        {
                            borderColor: shareMode === "single" ? colors.project.secondaryButton : colors.content.border.default,
                            backgroundColor: colors.content.container.subtle,
                            borderRadius: radiuses.md,
                            borderWidth: borders.line,
                            padding: spaces.spacingLg,
                            gap: spaces.spacingMd
                        }
                    ]}
                    onPress={() => {
                        setShareMode("single");
                        setModalStep("editor");
                    }}
                    activeOpacity={0.7}
                >
                    <View
                        style={[
                            stylesheet.selectionCardLeft,
                            {
                                gap: spaces.spacingMd
                            }
                        ]}
                    >
                        <View
                            style={[
                                stylesheet.selectionCardIcon,
                                {
                                    backgroundColor: colors.project.secondaryButton,
                                    borderRadius: radiuses.full
                                }
                            ]}
                        >
                            <ImageIcon
                                color={colors.project.brandWhite}
                                size={24}
                            />
                        </View>
                        <View
                            style={[
                                stylesheet.selectionCardBody,
                                {
                                    gap: spaces.spacingXs
                                }
                            ]}
                        >
                            <Text
                                customColor={colors.content.text.high}
                                variant="titleMediumSize"
                            >
                                {localize("instagram-share-mode-single")}
                            </Text>
                            <Text
                                customColor={colors.content.text.low}
                                variant="bodySmallSize"
                            >
                                {localize("instagram-share-mode-single-desc")}
                            </Text>
                        </View>
                    </View>
                    <ChevronRightIcon
                        color={colors.content.icon.mid}
                        size={20}
                    />
                </TouchableOpacity>
                {
                    allImages.length >= 2 ?
                        <TouchableOpacity
                            style={[
                                stylesheet.selectionCard,
                                {
                                    borderColor: shareMode === "carousel" ? colors.project.secondaryButton : colors.content.border.default,
                                    backgroundColor: colors.content.container.subtle,
                                    borderRadius: radiuses.md,
                                    borderWidth: borders.line,
                                    padding: spaces.spacingLg,
                                    gap: spaces.spacingMd
                                }
                            ]}
                            onPress={() => {
                                setShareMode("carousel");
                                setModalStep("editor");
                            }}
                            activeOpacity={0.7}
                        >
                            <View
                                style={[
                                    stylesheet.selectionCardLeft,
                                    {
                                        gap: spaces.spacingMd
                                    }
                                ]}
                            >
                                <View
                                    style={[
                                        stylesheet.selectionCardIcon,
                                        {
                                            backgroundColor: colors.project.secondaryButton,
                                            borderRadius: radiuses.full
                                        }
                                    ]}
                                >
                                    <LayersIcon
                                        color={colors.project.brandWhite}
                                        size={24}
                                    />
                                </View>
                                <View
                                    style={[
                                        stylesheet.selectionCardBody,
                                        {
                                            gap: spaces.spacingXs
                                        }
                                    ]}
                                >
                                    <Text
                                        customColor={colors.content.text.high}
                                        variant="titleMediumSize"
                                    >
                                        {`${localize("instagram-share-mode-carousel")} (${allImages.length})`}
                                    </Text>
                                    <Text
                                        customColor={colors.content.text.low}
                                        variant="bodySmallSize"
                                    >
                                        {localize("instagram-share-mode-carousel-desc")}
                                    </Text>
                                </View>
                            </View>
                            <ChevronRightIcon
                                color={colors.content.icon.mid}
                                size={20}
                            />
                        </TouchableOpacity>
                    :
                        null
                }
            </View>
        </View>;
    };

    const renderHeader = () => {
        return <View
            style={[
                stylesheet.headerContainer,
                {
                    marginBottom: spaces.spacingLg
                }
            ]}
        >
            <View
                style={[
                    stylesheet.titleContainer,
                    {
                        gap: spaces.spacingSm
                    }
                ]}
            >
                <CustomInstagramIcon
                    size={24}
                />
                <Text
                    variant="titleMediumSize"
                >
                    {modalStep === "select_mode" ? localize("instagram-share-select-type-title") : localize("instagram-share-modal-title")}
                </Text>
                {
                    modalStep === "editor" ?
                        <TouchableOpacity
                            style={[
                                stylesheet.modeBadgeButton,
                                {
                                    backgroundColor: colors.content.container.subtle,
                                    borderColor: colors.content.border.default,
                                    paddingHorizontal: spaces.spacingMd,
                                    paddingVertical: spaces.spacingXs,
                                    marginLeft: spaces.spacingSm,
                                    borderRadius: radiuses.full,
                                    borderWidth: borders.line,
                                    gap: spaces.spacingXs
                                }
                            ]}
                            onPress={() => setModalStep("select_mode")}
                        >
                            <ArrowLeftIcon
                                color={colors.content.icon.mid}
                                size={14}
                            />
                            <Text
                                customColor={colors.content.text.high}
                                variant="labelSmallSize"
                            >
                                {`${localize("instagram-share-change-mode")} (${shareMode === "triple" ? localize("instagram-share-mode-triple") : (shareMode === "carousel" ? localize("instagram-share-mode-carousel") : localize("instagram-share-mode-single"))})`}
                            </Text>
                        </TouchableOpacity>
                    :
                        null
                }
            </View>
            <TouchableOpacity
                style={stylesheet.closeButton}
                onPress={onClose}
            >
                <XIcon
                    color={colors.content.icon.high}
                    size={20}
                />
            </TouchableOpacity>
        </View>;
    };

    const renderEditor = () => {
        const isOverLimit = caption.length > 2200;

        return <View
            style={stylesheet.editorSection}
        >
            {
                isMultiLanguage ?
                    <View
                        style={[
                            stylesheet.languageSelectorContainer,
                            {
                                marginBottom: spaces.spacingMd,
                                gap: spaces.spacingSm
                            }
                        ]}
                    >
                        <Button
                            customBorderColor={selectedLangMode === "tr" ? colors.project.secondaryButton : colors.content.border.default}
                            customTextColor={selectedLangMode === "tr" ? colors.project.brandWhite : colors.content.text.high}
                            customColor={selectedLangMode === "tr" ? colors.project.secondaryButton : undefined}
                            variant={selectedLangMode === "tr" ? "filled" : "outline"}
                            onPress={() => handleLanguageChange("tr")}
                            title={localize("instagram-lang-tr")}
                            spreadBehaviour="free"
                            size="small"
                        />
                        <Button
                            customBorderColor={selectedLangMode === "en" ? colors.project.secondaryButton : colors.content.border.default}
                            customTextColor={selectedLangMode === "en" ? colors.project.brandWhite : colors.content.text.high}
                            customColor={selectedLangMode === "en" ? colors.project.secondaryButton : undefined}
                            variant={selectedLangMode === "en" ? "filled" : "outline"}
                            onPress={() => handleLanguageChange("en")}
                            title={localize("instagram-lang-en")}
                            spreadBehaviour="free"
                            size="small"
                        />
                        <Button
                            customBorderColor={selectedLangMode === "both" ? colors.project.secondaryButton : colors.content.border.default}
                            customTextColor={selectedLangMode === "both" ? colors.project.brandWhite : colors.content.text.high}
                            customColor={selectedLangMode === "both" ? colors.project.secondaryButton : undefined}
                            variant={selectedLangMode === "both" ? "filled" : "outline"}
                            onPress={() => handleLanguageChange("both")}
                            title={localize("instagram-lang-both")}
                            spreadBehaviour="free"
                            size="small"
                        />
                    </View>
                :
                    null
            }
            {
                (!isSelectedImageJpeg && shareMode === "single") || (!isAllTripleImagesJpeg && shareMode === "triple") ?
                    <View
                        style={[
                            stylesheet.formatWarningContainer,
                            {
                                backgroundColor: colors.system.container.warning,
                                marginBottom: spaces.spacingMd,
                                borderRadius: radiuses.md,
                                padding: spaces.spacingSm
                            }
                        ]}
                    >
                        <Text
                            customColor={colors.system.default.warning}
                            variant="bodySmallSize"
                        >
                            {`⚠️ ${localize("instagram-invalid-image-format-dialog")}`}
                        </Text>
                    </View>
                :
                    null
            }
            {
                isAlreadyPublished ?
                    <View
                        style={[
                            stylesheet.warningContainer,
                            {
                                backgroundColor: colors.system.container.warning,
                                marginBottom: spaces.spacingMd,
                                borderRadius: radiuses.md,
                                padding: spaces.spacingSm
                            }
                        ]}
                    >
                        <Text
                            customColor={colors.system.default.warning}
                            variant="bodySmallSize"
                        >
                            {localize("instagram-already-published-warning")}
                        </Text>
                    </View>
                :
                    null
            }
            {
                isOverLimit ?
                    <View
                        style={[
                            stylesheet.limitWarningContainer,
                            {
                                backgroundColor: colors.system.container.error,
                                marginBottom: spaces.spacingMd,
                                borderRadius: radiuses.md,
                                padding: spaces.spacingSm
                            }
                        ]}
                    >
                        <Text
                            customColor={colors.system.default.error}
                            variant="bodySmallSize"
                        >
                            {`⚠️ ${localize("instagram-char-limit-exceeded")} (${caption.length}/2200)`}
                        </Text>
                    </View>
                :
                    null
            }
            <Text
                style={{
                    marginBottom: spaces.spacingSm
                }}
                variant="labelLargeSize"
            >
                {localize("instagram-share-caption-label")}
            </Text>
            <TextAreaInput
                key={`instagram-caption-${targetID}-${selectedLangMode}-${isVisible ? "1" : "0"}`}
                placeholder={localize("instagram-share-caption-placeholder")}
                customBorderColor={colors.content.border.default}
                customColor={colors.project.inputBackground}
                inputStyle={{
                    color: colors.content.text.high
                }}
                contentContainerStyle={{
                    borderRadius: radiuses.md,
                    borderWidth: borders.line
                }}
                spreadBehaviour="stretch"
                isUpdateOnRealtime={true}
                onChangeText={setCaption}
                numberOfLines={8}
                ref={textAreaRef}
                value={caption}
                minHeight={180}
            />
            <View
                style={[
                    stylesheet.characterCountContainer,
                    {
                        marginTop: spaces.spacingXs
                    }
                ]}
            >
                <Text
                    customColor={isOverLimit ? colors.system.default.error : colors.content.text.low}
                    variant="bodySmallSize"
                >
                    {`${caption.length} / 2200`}
                </Text>
            </View>
            <View
                style={[
                    stylesheet.tagChipsContainer,
                    {
                        marginTop: spaces.spacingSm,
                        gap: spaces.spacingXs
                    }
                ]}
            >
                {
                    availableTags.map(tag => {
                        const isIncluded = caption.includes(tag);

                        return <TouchableOpacity
                            style={[
                                stylesheet.tagChip,
                                {
                                    borderColor: isIncluded ? colors.project.secondaryButton : colors.content.border.default,
                                    backgroundColor: isIncluded ? colors.project.secondaryButton : "transparent",
                                    paddingHorizontal: spaces.spacingSm,
                                    paddingVertical: spaces.spacingXs,
                                    borderRadius: radiuses.full,
                                    borderWidth: borders.line
                                }
                            ]}
                            onPress={() => handleToggleTag(tag)}
                            key={tag}
                        >
                            <Text
                                customColor={isIncluded ? colors.project.brandWhite : colors.content.text.mid}
                                variant="labelSmallSize"
                            >
                                {`${isIncluded ? "✓ " : "+ "}${tag}`}
                            </Text>
                        </TouchableOpacity>;
                    })
                }
            </View>
            <View
                style={[
                    stylesheet.titleContainer,
                    {
                        backgroundColor: colors.content.container.subtle,
                        borderColor: colors.content.border.default,
                        paddingHorizontal: spaces.spacingMd,
                        paddingVertical: spaces.spacingSm,
                        marginBottom: spaces.spacingSm,
                        marginTop: spaces.spacingMd,
                        borderRadius: radiuses.sm,
                        borderWidth: borders.line,
                        gap: spaces.spacingSm
                    }
                ]}
            >
                <Text
                    customColor={colors.content.text.low}
                    variant="labelMediumSize"
                >
                    {`${localize("instagram-share-mode-label")}:`}
                </Text>
                <Text
                    customColor={colors.content.text.high}
                    variant="labelLargeSize"
                >
                    {shareMode === "triple" ? localize("instagram-share-mode-triple") : (shareMode === "carousel" ? localize("instagram-share-mode-carousel") : localize("instagram-share-mode-single"))}
                </Text>
                <TouchableOpacity
                    onPress={() => setModalStep("select_mode")}
                    style={stylesheet.modeChangeButton}
                >
                    <Text
                        customColor={colors.project.gold}
                        variant="labelSmallSize"
                    >
                        {`[ ${localize("instagram-share-change-mode")} ]`}
                    </Text>
                </TouchableOpacity>
            </View>
            {
                shareMode === "carousel" ?
                    <View
                        style={[
                            stylesheet.carouselHintContainer,
                            {
                                backgroundColor: colors.content.container.subtle,
                                marginBottom: spaces.spacingSm,
                                borderRadius: radiuses.md,
                                padding: spaces.spacingSm
                            }
                        ]}
                    >
                        <Text
                            customColor={colors.content.text.mid}
                            variant="bodySmallSize"
                        >
                            {`ℹ️ ${localize("instagram-carousel-hint")}`}
                        </Text>
                    </View>
                :
                    null
            }
            {
                shareMode === "triple" ?
                    <View
                        style={{
                            marginTop: spaces.spacingSm
                        }}
                    >
                        <View
                            style={[
                                stylesheet.tripleHintContainer,
                                {
                                    backgroundColor: colors.content.container.subtle,
                                    marginBottom: spaces.spacingSm,
                                    borderRadius: radiuses.md,
                                    padding: spaces.spacingSm
                                }
                            ]}
                        >
                            <Text
                                customColor={colors.content.text.mid}
                                variant="bodySmallSize"
                            >
                                {`ℹ️ ${localize("instagram-triple-hint")}`}
                            </Text>
                        </View>
                        <Text
                            style={{
                                marginBottom: spaces.spacingSm
                            }}
                            variant="labelLargeSize"
                        >
                            {localize("instagram-triple-reorder-hint")}
                        </Text>
                        <View
                            style={[
                                stylesheet.tripleSlotsContainer,
                                {
                                    marginBottom: spaces.spacingMd,
                                    gap: spaces.spacingSm
                                }
                            ]}
                        >
                            {
                                [
                                    0,
                                    1,
                                    2
                                ].map(slotIndex => {
                                    const slotItem = tripleImages[slotIndex];
                                    const isDragged = draggedSlotIndex === slotIndex;
                                    const isActive = activeSlotIndex === slotIndex;

                                    const slotLabels = [
                                        localize("instagram-slot-right-cover"),
                                        localize("instagram-slot-middle"),
                                        localize("instagram-slot-left")
                                    ];

                                    const dndProps = Platform.OS === "web" ? {
                                        onDragStart: (e: React.DragEvent<HTMLElement>) => handleSlotDragStart(slotIndex, e),
                                        onDrop: (e: React.DragEvent<HTMLElement>) => handleSlotDrop(slotIndex, e),
                                        onDragOver: (e: React.DragEvent<HTMLElement>) => handleSlotDragOver(e),
                                        onDragEnd: handleSlotDragEnd,
                                        draggable: Boolean(slotItem)
                                    } : {};

                                    if(!slotItem) {
                                        return <TouchableOpacity
                                            style={[
                                                stylesheet.emptySlotContainer,
                                                {
                                                    borderColor: isActive ? colors.project.secondaryButton : colors.content.border.subtle,
                                                    backgroundColor: isActive ? colors.content.container.subtle : colors.system.shadow,
                                                    borderWidth: isActive ? borders.line * 2 : borders.line,
                                                    padding: spaces.spacingSm,
                                                    borderRadius: radiuses.sm,
                                                    gap: spaces.spacingXs
                                                }
                                            ]}
                                            onPress={() => setActiveSlotIndex(slotIndex)}
                                            key={`triple-empty-slot-${slotIndex}`}
                                        >
                                            <PlusIcon
                                                color={isActive ? colors.project.secondaryButton : colors.content.text.low}
                                                size={20}
                                            />
                                            <Text
                                                customColor={isActive ? colors.project.secondaryButton : colors.content.text.mid}
                                                variant="labelSmallSize"
                                            >
                                                {slotLabels[slotIndex]}
                                            </Text>
                                            <Text
                                                customColor={colors.content.text.low}
                                                variant="bodySmallSize"
                                            >
                                                {`+ ${localize("instagram-empty-slot-label")}`}
                                            </Text>
                                        </TouchableOpacity>;
                                    }

                                    return <TouchableOpacity
                                        style={[
                                            stylesheet.tripleSlotCard,
                                            isDragged ? stylesheet.dragActiveSlotCard : null,
                                            {
                                                borderColor: isActive ? colors.project.secondaryButton : colors.content.border.subtle,
                                                borderWidth: isActive ? borders.line * 2 : borders.line,
                                                backgroundColor: colors.system.shadow,
                                                borderRadius: radiuses.sm
                                            }
                                        ]}
                                        onPress={() => setActiveSlotIndex(slotIndex)}
                                        key={`triple-slot-${slotIndex}`}
                                        {...dndProps}
                                    >
                                        <SmartCoverImage
                                            containerStyle={stylesheet.tripleSlotImage}
                                            url={slotItem.displayUrl}
                                        />
                                        <TouchableOpacity
                                            onPress={() => handleRemoveSlotImage(slotIndex)}
                                            style={[
                                                stylesheet.slotRemoveButton,
                                                {
                                                    backgroundColor: colors.system.scrim,
                                                    borderRadius: radiuses.full
                                                }
                                            ]}
                                        >
                                            <XIcon
                                                color={colors.project.brandWhite}
                                                size={12}
                                            />
                                        </TouchableOpacity>
                                        <View
                                            style={[
                                                stylesheet.tripleSlotBadge,
                                                {
                                                    backgroundColor: colors.system.scrim,
                                                    paddingHorizontal: spaces.spacingXs,
                                                    paddingVertical: spaces.spacingXs,
                                                    borderRadius: radiuses.xs,
                                                    bottom: spaces.spacingXs,
                                                    left: spaces.spacingXs
                                                }
                                            ]}
                                        >
                                            <Text
                                                customColor={colors.project.brandWhite}
                                                variant="bodySmallSize"
                                            >
                                                {slotLabels[slotIndex]}
                                            </Text>
                                        </View>
                                        <View
                                            style={[
                                                stylesheet.tripleSlotActions,
                                                {
                                                    borderBottomRightRadius: radiuses.sm,
                                                    backgroundColor: colors.system.scrim,
                                                    borderBottomLeftRadius: radiuses.sm,
                                                    paddingHorizontal: spaces.spacingXs,
                                                    paddingVertical: spaces.spacingXs
                                                }
                                            ]}
                                        >
                                            <TouchableOpacity
                                                onPress={() => handleSwapSlots(slotIndex, slotIndex - 1)}
                                                style={[
                                                    stylesheet.tripleSlotArrowButton,
                                                    {
                                                        opacity: slotIndex === 0 ? 0.3 : 1
                                                    }
                                                ]}
                                                disabled={slotIndex === 0}
                                            >
                                                <ChevronLeftIcon
                                                    color={colors.project.brandWhite}
                                                    size={16}
                                                />
                                            </TouchableOpacity>
                                            <TouchableOpacity
                                                onPress={() => handleSwapSlots(slotIndex, slotIndex + 1)}
                                                style={[
                                                    stylesheet.tripleSlotArrowButton,
                                                    {
                                                        opacity: slotIndex === 2 ? 0.3 : 1
                                                    }
                                                ]}
                                                disabled={slotIndex === 2}
                                            >
                                                <ChevronRightIcon
                                                    color={colors.project.brandWhite}
                                                    size={16}
                                                />
                                            </TouchableOpacity>
                                        </View>
                                    </TouchableOpacity>;
                                })
                            }
                        </View>
                    </View>
                :
                    null
            }
            <View
                style={[
                    stylesheet.mediaSourceActions,
                    {
                        marginBottom: spaces.spacingMd,
                        marginTop: spaces.spacingSm,
                        gap: spaces.spacingSm
                    }
                ]}
            >
                <Button
                    customBorderColor={colors.project.secondaryButton}
                    title={localize("instagram-drive-select-button")}
                    customColor={colors.project.secondaryButton}
                    customTextColor={colors.project.brandWhite}
                    onPress={() => setIsDriveModalOpen(true)}
                    icon={() => <FolderIcon
                        color={colors.project.brandWhite}
                        size={16}
                    />}
                    spreadBehaviour="free"
                    variant="filled"
                    size="small"
                />
            </View>
            {
                allImages.length > 0 ?
                    <View
                        style={{
                            marginTop: spaces.spacingXs
                        }}
                    >
                        <Text
                            style={{
                                marginBottom: spaces.spacingSm
                            }}
                            variant="labelLargeSize"
                        >
                            {localize("instagram-available-images-title")}
                        </Text>
                        <View
                            style={[
                                stylesheet.imagePickerContainer,
                                {
                                    gap: spaces.spacingSm
                                }
                            ]}
                        >
                            {
                                allImages.map(item => {
                                    const tripleSlotIdx = tripleImages.findIndex(img => img?.key === item.key);
                                    const isSelectedInSingle = shareMode === "single" && selectedImage === item.url;
                                    const isSelectedInTriple = shareMode === "triple" && tripleSlotIdx !== -1;
                                    const isSelected = isSelectedInSingle || isSelectedInTriple;

                                    return <TouchableOpacity
                                        style={[
                                            stylesheet.imagePickerItem,
                                            {
                                                borderColor: isSelected ? colors.system.default.warning : colors.content.border.subtle,
                                                borderRadius: radiuses.sm,
                                                borderWidth: borders.line
                                            }
                                        ]}
                                        onPress={() => handleSelectImageForSlot(item)}
                                        key={item.key || item.url}
                                    >
                                        <Image
                                            style={stylesheet.imagePickerThumbnail}
                                            source={{
                                                uri: item.displayUrl
                                            }}
                                            resizeMode="cover"
                                        />
                                        {
                                            isSelectedInTriple ?
                                                <View
                                                    style={[
                                                        stylesheet.imagePickerBadge,
                                                        {
                                                            backgroundColor: colors.system.default.warning,
                                                            paddingHorizontal: spaces.spacingXs,
                                                            paddingVertical: spaces.spacingXs,
                                                            borderRadius: radiuses.full,
                                                            right: spaces.spacingXs,
                                                            top: spaces.spacingXs
                                                        }
                                                    ]}
                                                >
                                                    <Text
                                                        customColor={colors.project.brandWhite}
                                                        variant="labelSmallSize"
                                                    >
                                                        {`#${tripleSlotIdx + 1}`}
                                                    </Text>
                                                </View>
                                            :
                                                isSelectedInSingle ?
                                                    <View
                                                        style={[
                                                            stylesheet.imagePickerBadge,
                                                            {
                                                                backgroundColor: colors.system.default.warning,
                                                                paddingHorizontal: spaces.spacingXs,
                                                                paddingVertical: spaces.spacingXs,
                                                                borderRadius: radiuses.full,
                                                                right: spaces.spacingXs,
                                                                top: spaces.spacingXs
                                                            }
                                                        ]}
                                                    >
                                                        <CheckIcon
                                                            color={colors.project.brandWhite}
                                                            size={12}
                                                        />
                                                    </View>
                                            :
                                                null
                                        }
                                    </TouchableOpacity>;
                                })
                            }
                        </View>
                    </View>
                :
                    null
            }
        </View>;
    };

    const renderPreview = () => {
        let previewImageUrl = "";

        if(shareMode === "triple") {
            previewImageUrl = activePreviewTab === "grid" ? (tripleImages[0]?.displayUrl || "") : (tripleImages[activePreviewTab]?.displayUrl || "");
        } else if(shareMode === "carousel") {
            previewImageUrl = allImages[carouselPreviewIndex]?.displayUrl || getPhotoUrl(coverPhotoPath, authToken);
        } else {
            previewImageUrl = getPhotoUrl(selectedImage || coverPhotoPath, authToken);
        }

        const currentPostNumber = typeof activePreviewTab === "number" ? activePreviewTab + 1 : 1;

        return <View
            style={stylesheet.previewSection}
        >
            <Text
                style={{
                    marginBottom: spaces.spacingSm
                }}
                variant="labelLargeSize"
            >
                {localize("instagram-share-preview-title")}
            </Text>
            {
                shareMode === "triple" ?
                    <View
                        style={[
                            stylesheet.previewTabContainer,
                            {
                                marginBottom: spaces.spacingSm,
                                gap: spaces.spacingXs
                            }
                        ]}
                    >
                        <Button
                            customBorderColor={activePreviewTab === "grid" ? colors.project.secondaryButton : colors.content.border.default}
                            customTextColor={activePreviewTab === "grid" ? colors.project.brandWhite : colors.content.text.high}
                            customColor={activePreviewTab === "grid" ? colors.project.secondaryButton : undefined}
                            variant={activePreviewTab === "grid" ? "filled" : "outline"}
                            title={localize("instagram-grid-preview-title")}
                            onPress={() => setActivePreviewTab("grid")}
                            spreadBehaviour="free"
                            size="small"
                        />
                        <Button
                            customBorderColor={activePreviewTab === 0 ? colors.project.secondaryButton : colors.content.border.default}
                            customTextColor={activePreviewTab === 0 ? colors.project.brandWhite : colors.content.text.high}
                            customColor={activePreviewTab === 0 ? colors.project.secondaryButton : undefined}
                            variant={activePreviewTab === 0 ? "filled" : "outline"}
                            title={`1. ${localize("instagram-post-preview-tab")}`}
                            onPress={() => setActivePreviewTab(0)}
                            spreadBehaviour="free"
                            size="small"
                        />
                        <Button
                            customBorderColor={activePreviewTab === 1 ? colors.project.secondaryButton : colors.content.border.default}
                            customTextColor={activePreviewTab === 1 ? colors.project.brandWhite : colors.content.text.high}
                            customColor={activePreviewTab === 1 ? colors.project.secondaryButton : undefined}
                            variant={activePreviewTab === 1 ? "filled" : "outline"}
                            title={`2. ${localize("instagram-post-preview-tab")}`}
                            onPress={() => setActivePreviewTab(1)}
                            spreadBehaviour="free"
                            size="small"
                        />
                        <Button
                            customBorderColor={activePreviewTab === 2 ? colors.project.secondaryButton : colors.content.border.default}
                            customTextColor={activePreviewTab === 2 ? colors.project.brandWhite : colors.content.text.high}
                            customColor={activePreviewTab === 2 ? colors.project.secondaryButton : undefined}
                            variant={activePreviewTab === 2 ? "filled" : "outline"}
                            title={`3. ${localize("instagram-post-preview-tab")}`}
                            onPress={() => setActivePreviewTab(2)}
                            spreadBehaviour="free"
                            size="small"
                        />
                    </View>
                :
                    null
            }
            {
                shareMode === "triple" && activePreviewTab === "grid" ?
                    <View
                        style={[
                            stylesheet.instagramCard,
                            {
                                borderColor: colors.content.border.subtle,
                                backgroundColor: colors.system.shadow,
                                padding: spaces.spacingMd,
                                borderRadius: radiuses.sm,
                                borderWidth: borders.line
                            }
                        ]}
                    >
                        <View
                            style={[
                                stylesheet.instagramHeader,
                                {
                                    paddingBottom: spaces.spacingSm,
                                    gap: spaces.spacingSm
                                }
                            ]}
                        >
                            <Image
                                source={{
                                    uri: "https://gencmusiad.org.tr/favicon.ico"
                                }}
                                style={[
                                    stylesheet.instagramAvatar,
                                    {
                                        borderRadius: radiuses.full
                                    }
                                ]}
                            />
                            <View
                                style={stylesheet.instagramUsernameContainer}
                            >
                                <Text
                                    customColor={colors.project.brandWhite}
                                    variant="labelMediumSize"
                                >
                                    {"gencmusiad"}
                                </Text>
                                <Text
                                    customColor={colors.content.text.low}
                                    variant="bodySmallSize"
                                >
                                    {localize("instagram-grid-preview-title")}
                                </Text>
                            </View>
                        </View>
                        <View
                            style={[
                                stylesheet.gridPreviewContainer,
                                {
                                    backgroundColor: colors.system.shadow,
                                    borderRadius: radiuses.sm,
                                    gap: spaces.spacingXs
                                }
                            ]}
                        >
                            {
                                [
                                    2,
                                    1,
                                    0
                                ].map(slotIdx => {
                                    const item = tripleImages[slotIdx];
                                    const labels = [
                                        localize("instagram-slot-right-cover"),
                                        localize("instagram-slot-middle"),
                                        localize("instagram-slot-left")
                                    ];

                                    return <TouchableOpacity
                                        onPress={() => setActivePreviewTab(slotIdx as 0 | 1 | 2)}
                                        style={[
                                            stylesheet.gridPreviewItem,
                                            {
                                                backgroundColor: colors.content.container.subtle,
                                                borderRadius: radiuses.xs
                                            }
                                        ]}
                                        key={`grid-preview-slot-${slotIdx}`}
                                    >
                                        {
                                            item?.displayUrl ?
                                                <SmartCoverImage
                                                    containerStyle={stylesheet.tripleSlotImage}
                                                    url={item.displayUrl}
                                                />
                                            :
                                                null
                                        }
                                        <View
                                            style={[
                                                stylesheet.gridPreviewBadge,
                                                {
                                                    backgroundColor: colors.system.scrim,
                                                    paddingHorizontal: spaces.spacingXs,
                                                    paddingVertical: spaces.spacingXs,
                                                    borderRadius: radiuses.xs,
                                                    bottom: spaces.spacingXs,
                                                    left: spaces.spacingXs
                                                }
                                            ]}
                                        >
                                            <Text
                                                customColor={colors.project.brandWhite}
                                                variant="bodySmallSize"
                                            >
                                                {labels[slotIdx]}
                                            </Text>
                                        </View>
                                    </TouchableOpacity>;
                                })
                            }
                        </View>
                        <Text
                            customColor={colors.content.text.low}
                            style={{
                                marginTop: spaces.spacingSm
                            }}
                            variant="bodySmallSize"
                        >
                            {localize("instagram-grid-preview-hint")}
                        </Text>
                    </View>
                :
                    <View
                        style={[
                            stylesheet.instagramCard,
                            {
                                borderColor: colors.content.border.subtle,
                                backgroundColor: colors.system.shadow,
                                padding: spaces.spacingMd,
                                borderRadius: radiuses.sm,
                                borderWidth: borders.line
                            }
                        ]}
                    >
                        <View
                            style={[
                                stylesheet.instagramHeader,
                                {
                                    paddingBottom: spaces.spacingSm,
                                    gap: spaces.spacingSm
                                }
                            ]}
                        >
                            <Image
                                source={{
                                    uri: "https://gencmusiad.org.tr/favicon.ico"
                                }}
                                style={[
                                    stylesheet.instagramAvatar,
                                    {
                                        borderRadius: radiuses.full
                                    }
                                ]}
                            />
                            <View
                                style={stylesheet.instagramUsernameContainer}
                            >
                                <Text
                                    customColor={colors.project.brandWhite}
                                    variant="labelMediumSize"
                                >
                                    {"gencmusiad"}
                                </Text>
                                <Text
                                    customColor={colors.content.text.low}
                                    variant="bodySmallSize"
                                >
                                    {shareMode === "triple" ? `${currentPostNumber}. ${localize("instagram-post-preview-tab")}` : "Türkiye"}
                                </Text>
                            </View>
                        </View>
                        <View
                            style={[
                                stylesheet.instagramMediaContainer,
                                {
                                    backgroundColor: colors.system.shadow
                                }
                            ]}
                        >
                            {
                                previewImageUrl ?
                                    <SmartCoverImage
                                        containerStyle={stylesheet.postMedia}
                                        url={previewImageUrl}
                                    />
                                :
                                    <Text
                                        customColor={colors.content.text.low}
                                        variant="bodySmallSize"
                                    >
                                        {localize("instagram-no-image-selected")}
                                    </Text>
                            }
                            {
                                shareMode === "carousel" && allImages.length > 1 ?
                                    <>
                                        <View
                                            style={[
                                                stylesheet.carouselBadgeContainer,
                                                {
                                                    backgroundColor: colors.system.scrim,
                                                    paddingHorizontal: spaces.spacingSm,
                                                    paddingVertical: spaces.spacingXs,
                                                    borderRadius: radiuses.full,
                                                    right: spaces.spacingSm,
                                                    top: spaces.spacingSm
                                                }
                                            ]}
                                        >
                                            <Text
                                                customColor={colors.project.brandWhite}
                                                variant="bodySmallSize"
                                            >
                                                {`${localize("instagram-carousel-badge")} (${carouselPreviewIndex + 1}/${allImages.length})`}
                                            </Text>
                                        </View>
                                        {
                                            carouselPreviewIndex > 0 ?
                                                <TouchableOpacity
                                                    onPress={() => setCarouselPreviewIndex(prev => prev - 1)}
                                                    style={[
                                                        stylesheet.carouselArrowButton,
                                                        {
                                                            backgroundColor: colors.system.scrim,
                                                            borderRadius: radiuses.full,
                                                            left: spaces.spacingSm
                                                        }
                                                    ]}
                                                >
                                                    <ChevronLeftIcon
                                                        color={colors.project.brandWhite}
                                                        size={16}
                                                    />
                                                </TouchableOpacity>
                                            :
                                                null
                                        }
                                        {
                                            carouselPreviewIndex < allImages.length - 1 ?
                                                <TouchableOpacity
                                                    onPress={() => setCarouselPreviewIndex(prev => prev + 1)}
                                                    style={[
                                                        stylesheet.carouselArrowButton,
                                                        {
                                                            backgroundColor: colors.system.scrim,
                                                            borderRadius: radiuses.full,
                                                            right: spaces.spacingSm
                                                        }
                                                    ]}
                                                >
                                                    <ChevronRightIcon
                                                        color={colors.project.brandWhite}
                                                        size={16}
                                                    />
                                                </TouchableOpacity>
                                            :
                                                null
                                        }
                                        <View
                                            style={[
                                                stylesheet.carouselNavContainer,
                                                {
                                                    bottom: spaces.spacingSm,
                                                    gap: spaces.spacingXs
                                                }
                                            ]}
                                        >
                                            {
                                                allImages.map((_, dotIdx) => {
                                                    const isCurrentDot = dotIdx === carouselPreviewIndex;

                                                    return <View
                                                        style={[
                                                            stylesheet.carouselNavDot,
                                                            {
                                                                backgroundColor: isCurrentDot ? colors.project.brandWhite : colors.system.scrim,
                                                                borderRadius: radiuses.full
                                                            }
                                                        ]}
                                                        key={`carousel-nav-dot-${dotIdx}`}
                                                    />;
                                                })
                                            }
                                        </View>
                                    </>
                                :
                                    null
                            }
                        </View>
                        <View
                            style={[
                                stylesheet.instagramActionsBar,
                                {
                                    paddingTop: spaces.spacingSm,
                                    gap: spaces.spacingMd
                                }
                            ]}
                        >
                            <HeartIcon
                                color={colors.project.brandWhite}
                                size={18}
                            />
                            <MessageCircleIcon
                                color={colors.project.brandWhite}
                                size={18}
                            />
                            <SendIcon
                                color={colors.project.brandWhite}
                                size={18}
                            />
                            <View
                                style={stylesheet.bookmarkContainer}
                            >
                                <BookmarkIcon
                                    color={colors.project.brandWhite}
                                    size={18}
                                />
                            </View>
                        </View>
                        <ScrollView
                            style={[
                                stylesheet.instagramCaptionContainer,
                                {
                                    marginTop: spaces.spacingSm
                                }
                            ]}
                            showsVerticalScrollIndicator={false}
                        >
                            <Text
                                customColor={colors.project.brandWhite}
                                variant="bodySmallSize"
                            >
                                <Text
                                    customColor={colors.project.brandWhite}
                                    variant="labelMediumSize"
                                >
                                    {"gencmusiad "}
                                </Text>
                                {caption}
                            </Text>
                        </ScrollView>
                    </View>
            }
            <Text
                customColor={colors.content.text.low}
                style={{
                    marginTop: spaces.spacingSm
                }}
                variant="bodySmallSize"
            >
                {localize("instagram-aspect-ratio-hint")}
            </Text>
        </View>;
    };

    const renderFooter = () => {
        if(modalStep === "select_mode") {
            return <View
                style={[
                    stylesheet.footerContainer,
                    {
                        marginTop: spaces.spacingLg,
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <Button
                    customBorderColor={colors.project.secondaryButton}
                    customColor={colors.project.secondaryButton}
                    customTextColor={colors.project.brandWhite}
                    title={localize("cancel")}
                    spreadBehaviour="free"
                    onPress={onClose}
                    variant="filled"
                />
            </View>;
        }

        return <View
            style={[
                stylesheet.footerContainer,
                {
                    marginTop: spaces.spacingLg,
                    gap: spaces.spacingMd
                }
            ]}
        >
            <Button
                customBorderColor={colors.project.secondaryButton}
                customColor={colors.project.secondaryButton}
                customTextColor={colors.project.brandWhite}
                title={localize("cancel")}
                isDisabled={isSubmitting}
                spreadBehaviour="free"
                onPress={onClose}
                variant="filled"
            />
            <Button
                isDisabled={isSubmitting || caption.trim().length === 0 || caption.length > 2200}
                onPress={() => setIsConfirmDialogOpen(true)}
                customTextColor={colors.project.brandWhite}
                title={localize("share-on-instagram")}
                icon={() => <CustomInstagramIcon
                    color={colors.project.brandWhite}
                    size={16}
                />}
                isLoading={isSubmitting}
                spreadBehaviour="free"
                customColor="primary"
                variant="filled"
            />
        </View>;
    };

    return <>
        <Modal
            id="share-instagram-modal"
            portalName="modal-system"
            isActive={isVisible}
            onClosed={onClose}
        >
            <View
                style={[
                    stylesheet.container,
                    {
                        backgroundColor: colors.content.container.default,
                        padding: spaces.spacingXl,
                        borderRadius: radiuses.lg,
                        gap: spaces.spacingLg
                    }
                ]}
            >
                {renderHeader()}
                {
                    modalStep === "select_mode" ?
                        renderSelectMode()
                    :
                        <View
                            style={[
                                stylesheet.bodyContainer,
                                {
                                    gap: spaces.spacingLg
                                },
                                isMobile ? {
                                    flexDirection: "column"
                                } : null
                            ]}
                        >
                            {renderEditor()}
                            {renderPreview()}
                        </View>
                }
                {renderFooter()}
            </View>
        </Modal>
        <Dialog
            content={
                shareMode === "triple"
                    ? localize("share-instagram-confirm-triple-desc")
                    : (shareMode === "carousel" ? localize("share-instagram-confirm-carousel-desc") : localize("share-instagram-confirm-desc"))
            }
            primaryButtonProps={{
                title: localize("share-instagram-confirm-approve"),
                isLoading: isSubmitting,
                onPress: handleShare
            }}
            secondaryButtonProps={{
                title: localize("share-instagram-confirm-cancel"),
                onPress: () => setIsConfirmDialogOpen(false),
                customTextColor: colors.project.brandWhite,
                customBorderColor: "secondaryButton",
                customColor: "secondaryButton",
                variant: "filled"
            }}
            title={localize("share-instagram-confirm-title")}
            onClosed={() => setIsConfirmDialogOpen(false)}
            isVisible={isConfirmDialogOpen}
            variant="yes-no"
        />
        <DriveSelectorModal
            onClose={() => setIsDriveModalOpen(false)}
            onSelect={handleDriveSelect}
            isVisible={isDriveModalOpen}
        />
    </>;
};
export default ShareInstagramModal;
